import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Toaster, toast } from 'react-hot-toast'
import AuthHeader from '../../components/company/registration/AuthHeader'
import RegistrationSidebar from '../../components/company/registration/RegistrationSidebar'
import StepIndicator from '../../components/company/registration/StepIndicator'
import CompanyDetailsForm from './steps/CompanyDetailsForm'
import { companyService } from '../../services/companyService'
import { authService } from '../../services/authService'
import { companyRegistrationSteps } from '../../utils/companyRegistrationSteps'
import { mapCompanyRegisterToForm } from '../../utils/dataMapping'

const LegalEntityDetailsPage = () => {
    const navigate = useNavigate()
    const formRef = React.useRef(null)
    const [isLoading, setIsLoading] = useState(false)
    const [formData, setFormData] = useState({})
    const [userId, setUserId] = useState(null)
    const [user, setUser] = useState(null)
    const [companyId, setCompanyId] = useState(null)

    // Current Step: 1
    const currentStepConfig = companyRegistrationSteps.find(s => s.id === 1)

    useEffect(() => {
        const checkStatus = async () => {
            try {
                const currentUser = await authService.getCurrentUser()
                if (!currentUser) {
                    navigate('/auth/login')
                    return
                }
                setUser(currentUser)
                setUserId(currentUser.id || currentUser.user_id)

                // Check for company_id in user object or session
                const existingCompanyId = currentUser.company_id || sessionStorage.getItem('companyId')

                if (existingCompanyId) {
                    setCompanyId(existingCompanyId)
                    // Fetch existing data
                    try {
                        // Using user ID for fetching as per new API signature
                        const data = await companyService.getCompanyRegisterData(currentUser.id || currentUser.user_id)
                        if (data) {
                            // Map API data to form structure
                            setFormData(mapCompanyRegisterToForm(data))
                        }
                    } catch (err) {
                        console.error("Failed to fetch existing company data", err)
                    }
                }

                // Check registration status - enforce forward progress on reload/login
                // Use strict check to prevent getting stuck on Step 1 if already advanced
                const status = currentUser.hasCompletedRegistration
                const s = String(status)

                if (s === 'true' || s === 'TRUE' || status === true) {
                    navigate('/company/registration/documents')
                    return
                }
                if (s === 'company-profile') {
                    navigate('/company/registration/subscription')
                    return
                }
                if (s === 'company-register') {
                    navigate('/company/registration/profile')
                    return
                }

            } catch (error) {
                console.error("Status check failed", error)
            }
        }
        checkStatus()
    }, [navigate])

    const handleNext = async (stepData) => {
        try {
            setIsLoading(true)

            let response;
            // Always use saveLegalDetails (POST) which handles upsert/register logic
            response = await companyService.saveLegalDetails(stepData)
            toast.success('Company details saved successfully!')

            // Update local storage / session
            const newCompanyId = response.company_id || response.data?.company_id || companyId
            if (newCompanyId) {
                sessionStorage.setItem('companyId', newCompanyId)
                if (!companyId) setCompanyId(newCompanyId) // Update state if it was a new creation
            }

            // Update user status in session
            if (user) {
                const updatedUser = { ...user, hasCompletedRegistration: 'company-register', company_id: newCompanyId }
                sessionStorage.setItem('userData', JSON.stringify(updatedUser))
                sessionStorage.setItem('hasCompletedRegistration', JSON.stringify('company-register'))
            }

            navigate('/company/registration/profile')

        } catch (error) {
            console.error(error)
            toast.error(error.message || 'Failed to save details')
        } finally {
            setIsLoading(false)
        }
    }

    const triggerSubmit = () => {
        formRef.current?.submitForm()
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-secondary-900 dark:via-secondary-800 dark:to-secondary-900">
            <AuthHeader />

            <div className="flex flex-col items-center min-h-[calc(100vh-64px)] p-4 py-8 gap-6">
                {/* Step Indicator - Outside Card */}
                <div className="w-full max-w-6xl">
                    <StepIndicator currentStep={1} totalSteps={companyRegistrationSteps.length} />
                </div>

                <div className="flex items-start justify-center w-full gap-6">
                    {/* Sidebar - Left Side */}
                    <div className="hidden lg:block sticky top-24">
                        <RegistrationSidebar
                            currentStep={1}
                        />
                    </div>

                    {/* Main Card */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="card w-full max-w-6xl p-6 md:p-8 shadow-glow flex-1 bg-white/80 dark:bg-slate-800/90 backdrop-blur-xl border border-secondary-200 dark:border-secondary-700 rounded-2xl"
                    >
                        {/* Header */}
                        <div className="text-center mb-8 pb-6 border-b border-secondary-200 dark:border-secondary-700">
                            <h1 className="text-3xl font-display font-bold text-secondary-900 dark:text-secondary-100 mb-2 tracking-tight">
                                {currentStepConfig?.title || 'Company Details'}
                            </h1>
                            <p className="text-sm text-secondary-600 dark:text-secondary-400 max-w-md mx-auto leading-relaxed">
                                {currentStepConfig?.description || 'Legal entity information and registration details'}
                            </p>
                        </div>

                        {/* Form Content */}
                        <CompanyDetailsForm
                            ref={formRef}
                            formData={formData}
                            onNext={handleNext}
                            isLoading={isLoading}
                            userId={userId}
                            userType="company"
                            userData={user}
                        />

                        {/* Footer / Action Buttons */}
                        <div className="mt-8 flex justify-end pt-6 border-t border-secondary-200 dark:border-secondary-700">
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={triggerSubmit}
                                disabled={isLoading}
                                className="px-8 py-3 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-medium rounded-xl shadow-lg shadow-primary-500/30 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            >
                                {isLoading ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        <span>Saving...</span>
                                    </>
                                ) : (
                                    <>
                                        <span>Continue to Profile</span>
                                    </>
                                )}
                            </motion.button>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    )
}

export default LegalEntityDetailsPage
